import React from 'react';
import { DocsIcon, LinkButton } from '@backstage/core-components';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { alpha, makeStyles, Typography } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { WelcomeTitle } from './WelcomeTitle';

const useStyles = makeStyles(theme => ({
  header: {
    gridArea: 'pageHeader',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '28px 40px 31px',
    [theme.breakpoints.down('sm')]: { padding: '23px 19px' },
  },
  top: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2.5),
    [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
  },
  intro: { minWidth: 0 },
  eyebrow: {
    color: theme.palette.navigation.selectedColor,
    fontSize: 10,
    fontWeight: 650,
    textTransform: 'uppercase',
    letterSpacing: 0,
    margin: 0,
  },
  title: {
    '&&': {
    fontSize: 30,
    fontWeight: 650,
    lineHeight: 1.2,
    letterSpacing: 0,
    margin: '9px 0',
    overflowWrap: 'anywhere',
    color: 'inherit',
    [theme.breakpoints.down('xs')]: { fontSize: 25 },
    },
  },
  subtitle: {
    fontSize: 13,
    color: alpha(theme.palette.primary.contrastText, 0.8),
    margin: 0,
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: 6,
    flexShrink: 0,
  },
  action: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'none',
    letterSpacing: 0,
    borderRadius: 5,
    padding: '8px 13px',
    boxShadow: 'none',
  },
  primaryAction: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: theme.palette.grey[100] },
  },
  secondaryAction: {
    color: 'inherit',
    borderColor: alpha(theme.palette.primary.contrastText, 0.3),
    '&:hover': { borderColor: theme.palette.primary.contrastText },
  },
  search: { maxWidth: 700, width: '100%', margin: '24px auto 0' },
  searchInput: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 28,
    boxShadow: theme.shadows[1],
    fontSize: 13,
  },
  searchOutline: { border: 0 },
}));

export function HomePageHeader() {
  const classes = useStyles();
  return (
    <header className={classes.header} aria-label="Welcome to DH Dock">
      <div className={classes.top}>
        <div className={classes.intro}>
          <Typography className={classes.eyebrow}>Your Digital Hangar, connected</Typography>
          <Typography component="h1" className={classes.title}><WelcomeTitle /></Typography>
          <Typography className={classes.subtitle}>
            Discover our landscape. Understand the connections. Find your place.
          </Typography>
        </div>
        <div className={classes.actions}>
          <LinkButton
            className={`${classes.action} ${classes.primaryAction}`}
            startIcon={<AddCircleOutlineIcon />}
            variant="contained"
            to="/docs/default/System/be26df2f-284c-f011-877a-7c1e527224bf/tutorials/onboarding/"
          >Get onboarded</LinkButton>
          <LinkButton
            className={`${classes.action} ${classes.secondaryAction}`}
            startIcon={<DocsIcon />}
            variant="outlined"
            to="/docs/default/System/be26df2f-284c-f011-877a-7c1e527224bf"
          >Read the docs</LinkButton>
        </div>
      </div>
      <div className={classes.search}>
        <HomePageSearchBar
          InputProps={{ classes: { root: classes.searchInput, notchedOutline: classes.searchOutline } }}
          placeholder="Search products, components, capabilities, teams, docs..."
        />
      </div>
    </header>
  );
}